import type { TablePaginationConfig } from 'antd';
import type {
    FilterValue,
    SorterResult,
    SortOrder,
    TableCurrentDataSource,
} from 'antd/lib/table/interface';
import { useEffect, useState } from 'react';
import type { DefaultApiParams } from '@/std/pagination/types';
import type { SimNao } from '@/types';

type TablePagination = {
    /** Página atual */
    page: number;
    /** Quantidade de registros por página */
    pageSize: number;
    /** Total de registros */
    total: number;
    /** Se deve solicitar ao back a quantidade total de registros */
    getTotal: SimNao;
    /** A coluna e a direção que a consulta deve ser ordenada */
    order: string | undefined;
};

export type SetPaginationGetTotal = (getTotal: SimNao) => void;

export type OnTableChange = <T>(
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null> | undefined,
    sorter: SorterResult<T> | SorterResult<T>[],
    extra: TableCurrentDataSource<T>,
) => void;

const DEFAULT_PAGINATION: TablePagination = {
    getTotal: 'S',
    order: undefined,
    page: 1,
    pageSize: 10,
    total: 0,
};

/**  Controla a paginação de uma tabela do antd, sincronizando com a api
 * @param baseParams - Outros parâmetros (fora de paginação) a serem enviados para a api
 * @param initialPagination - Paginação inicial personalizada
 */
export function usePagination<P extends Partial<DefaultApiParams>>(
    baseParams?: P,
    initialPagination: Partial<TablePagination> = {},
) {
    const [pagination, setPagination] = useState<TablePagination>({
        ...DEFAULT_PAGINATION,
        ...initialPagination,
    });

    const params = {
        ...baseParams,
        orderby: pagination.order,
        qtde_registros: pagination.pageSize,
        registro_inicial: pagination.pageSize * (pagination.page - 1),
        total_registros: pagination.getTotal,
    } as P;

    const showTotal = (total: number) => {
        return `Total de registros: ${total}`;
    };

    const tablePagination: TablePaginationConfig = {
        current: pagination.page,
        pageSize: pagination.pageSize,
        showSizeChanger: pagination.total > 10,
        showTotal,
        total: pagination.total,
    };

    const onTableChange: OnTableChange = <T>(
        newPagination: TablePaginationConfig,
        _: Record<string, FilterValue | null> | undefined,
        sorter: SorterResult<T> | SorterResult<T>[],
        extra: TableCurrentDataSource<T>,
    ): void => {
        let order: string | undefined;

        if (extra.action === 'sort') {
            let field: string | undefined;
            let direction: SortOrder | undefined;

            if (Array.isArray(sorter)) {
                field = sorter[0]?.field as string;
                direction = sorter[0]?.order;
            } else {
                field = sorter.field as string;
                direction = sorter.order;
            }

            if (field && direction) {
                order = `${field} ${direction === 'ascend' ? 'asc' : 'desc'}`;
            }
        }

        setPagination((old) => ({
            ...old,
            getTotal: 'N',
            order: extra.action === 'paginate' ? old.order : order,
            page: newPagination.current || old.page,
            pageSize: newPagination.pageSize || old.pageSize,
        }));
    };

    /** Utilizado para atualizar o total de registros.
     * Pode ser enviado os registros ou o total de registros diretamente */
    const updateTotalAndPagination = (total: number | undefined): void => {
        if (total === undefined || total === null) {
            return;
        }

        setPagination((old) => {
            const pageSize = old.pageSize || 10;
            const totalPages = Math.max(1, Math.ceil(total / pageSize));
            const page = Math.min(old.page, totalPages);
            const shouldRefresh = page !== old.page;

            return {
                ...old,
                getTotal: shouldRefresh ? 'S' : old.getTotal,
                page,
                total,
            };
        });
    };

    const setGetTotal: SetPaginationGetTotal = (getTotal) => {
        setPagination((old) => ({ ...old, getTotal }));
    };

    useEffect(() => {
        setPagination((old) => ({
            ...old,
            getTotal: 'S',
            page: 1,
        }));
        // oxlint-disable-next-line react/exhaustive-effect-dependencies  é necessário observar mudanças em baseParams
    }, [baseParams]);

    return {
        onTableChange,
        params,
        setGetTotal,
        tablePagination,
        updateTotalAndPagination,
    };
}
